import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight, ChevronDown, Copy } from "lucide-react";

const WD_SHORT = ["Seg", "Ter", "Qua", "Qui", "Sex"];
const WD_PLURAL = ["Segundas", "Terças", "Quartas", "Quintas", "Sextas"];
const pad = (n) => String(n).padStart(2, "0");

export const defaultDay = () => ({ include: true, wType: "model", modelId: "", libItems: [], manualText: "" });

export const monthDays = (month, weekdays) => {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  const out = [];
  for (let d = 1; d <= last; d++) {
    const wd = (new Date(y, m - 1, d).getDay() + 6) % 7;
    if (weekdays.includes(wd)) out.push({ date: `${y}-${pad(m)}-${pad(d)}`, wd, label: `${WD_SHORT[wd]} ${pad(d)}/${pad(m)}` });
  }
  return out;
};

export const shiftMonth = (month, delta) => {
  const [y, m] = month.split("-").map(Number);
  const dt = new Date(y, m - 1 + delta, 1);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}`;
};

function DayCard({ d, st, open, onToggleOpen, setField, applyAll, templates, exercises, WorkoutFields }) {
  const summary = !st.include ? "Não incluído" : st.wType === "model" ? (templates.find((t) => t.id === st.modelId)?.name || "Escolha o treino")
    : st.wType === "library" ? `${st.libItems.length} exercício(s) da biblioteca` : (st.manualText ? "Treino à mão" : "Treino à mão (vazio)");
  return (
    <div data-testid={`month-day-${d.date}`} className={`rounded-xl border ${st.include ? "border-[#263147] bg-[#0F1520]" : "border-dashed border-[#263147] bg-[#0F1520]/40 opacity-60"}`}>
      <div className="flex items-center gap-2 p-2.5">
        <button type="button" data-testid={`month-day-open-${d.date}`} onClick={onToggleOpen} className="flex-1 min-w-0 flex items-center gap-2 text-left">
          <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
          <span className="font-semibold text-sm text-slate-100 shrink-0">{d.label}</span>
          <span className="text-xs text-slate-500 truncate">{summary}</span>
        </button>
        <button type="button" data-testid={`month-day-include-${d.date}`} onClick={() => setField("include", !st.include)}
          className={`h-7 px-2.5 rounded-lg text-[11px] font-medium border transition-colors shrink-0 ${st.include ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-[#181F2C] text-slate-400 border-[#263147]"}`}>
          {st.include ? "Incluído" : "Incluir"}
        </button>
      </div>
      {open && st.include && (
        <div className="px-2.5 pb-3 space-y-3 border-t border-[#263147] pt-3 min-w-0">
          <WorkoutFields wType={st.wType} setWType={(v) => setField("wType", v)} modelId={st.modelId} setModelId={(v) => setField("modelId", v)}
            templates={templates} exercises={exercises} libItems={st.libItems} setLibItems={(v) => setField("libItems", v)}
            manualText={st.manualText} setManualText={(v) => setField("manualText", v)} />
          <Button type="button" variant="outline" data-testid={`month-apply-all-${d.date}`} onClick={applyAll}
            className="w-full h-9 border-[#263147] bg-[#181F2C] text-slate-200 hover:bg-[#1E2738] rounded-lg text-xs">
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Aplicar para todas as {WD_PLURAL[d.wd]}
          </Button>
        </div>
      )}
    </div>
  );
}

export function MonthlyDays({ month, setMonth, weekdays, days, setDays, templates, exercises, WorkoutFields }) {
  const [openDate, setOpenDate] = useState(null);
  const list = monthDays(month, weekdays);
  const [y, m] = month.split("-").map(Number);
  const rawLbl = new Date(y, m - 1, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  const raw = rawLbl.charAt(0).toUpperCase() + rawLbl.slice(1);
  const get = (date) => days[date] || defaultDay();
  const setField = (date) => (field, v) => setDays((prev) => {
    const cur = prev[date] || defaultDay();
    return { ...prev, [date]: { ...cur, [field]: typeof v === "function" ? v(cur[field]) : v } };
  });
  const applyAll = (d) => () => setDays((prev) => {
    const src = prev[d.date] || defaultDay();
    const next = { ...prev };
    list.filter((x) => x.wd === d.wd && x.date !== d.date).forEach((x) => {
      next[x.date] = { ...src, include: (prev[x.date] || defaultDay()).include, libItems: src.libItems.map((it) => ({ ...it, key: Math.random().toString(36).slice(2) })) };
    });
    return next;
  });
  return (
    <div className="space-y-2 min-w-0" data-testid="month-days">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-slate-300">Dias do mês</Label>
        <div className="flex items-center gap-1">
          <Button type="button" variant="outline" size="icon" data-testid="month-prev" onClick={() => setMonth(shiftMonth(month, -1))} className="h-8 w-8 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-lg"><ChevronLeft className="w-4 h-4" /></Button>
          <span data-testid="month-label" className="text-sm font-medium text-slate-200 min-w-[8.5rem] text-center">{raw}</span>
          <Button type="button" variant="outline" size="icon" data-testid="month-next" onClick={() => setMonth(shiftMonth(month, 1))} className="h-8 w-8 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-lg"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-6 border border-dashed border-[#263147] rounded-xl">Escolha os dias da semana acima.</p>
      ) : list.map((d) => (
        <DayCard key={d.date} d={d} st={get(d.date)} open={openDate === d.date} onToggleOpen={() => setOpenDate(openDate === d.date ? null : d.date)}
          setField={setField(d.date)} applyAll={applyAll(d)} templates={templates} exercises={exercises} WorkoutFields={WorkoutFields} />
      ))}
    </div>
  );
}
